import React from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { StepAccess } from '../../../types/backend';
import { grantLabel } from '../utils/toolGrants';

interface Props {
  steps: StepAccess[];
}

const toolsNeeded = (step: StepAccess) =>
  (step.tools ?? []).filter((t) => !t.permitted);

// A step the operator cannot do, as one alert: an error when every run reaches it, a warning
// when only some route does. Names each tool and mode it lacks, and the backend's reason.
export const StepAccessGap: React.FC<{ step: StepAccess }> = ({ step }) => (
  <Alert severity={step.mandatory ? 'error' : 'warning'} sx={{ py: 0 }}>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {step.mandatory ? `Cannot do: ${step.label}` : `${step.label}: only needed if the run takes a route to stage ${step.layer}`}
    </Typography>
    {toolsNeeded(step).map((tool) => (
      <Typography key={tool.grant} variant="caption" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
        Needs {grantLabel(tool.grant)} ({tool.grant}): {tool.reason}
      </Typography>
    ))}
    {step.reason && (
      <Typography variant="caption" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
        {step.reason}
      </Typography>
    )}
  </Alert>
);

// Every step of a workflow by stage, against the operator's access: which they can do and which not.
const WorkflowAccessSteps: React.FC<Props> = ({ steps }) => {
  const layers = [...new Set(steps.map((s) => s.layer))].sort((a, b) => a - b);
  return (
    <Stack spacing={1.5}>
      {layers.map((layer) => (
        <Box key={layer} component="section" aria-label={`Stage ${layer} access`}>
          <Typography variant="subtitle2" component="h3">
            Stage {layer}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {steps
              .filter((s) => s.layer === layer)
              .map((step) =>
                step.permitted ? (
                  <Stack key={step.node_id} direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon fontSize="small" color="success" aria-hidden />
                    <Typography variant="body2">Can do: {step.label}</Typography>
                  </Stack>
                ) : (
                  <StepAccessGap key={step.node_id} step={step} />
                ),
              )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export default WorkflowAccessSteps;
